/**
 * useRecommendations (P192/193) -- the prioritised "so what?" remediation
 * list across the diagnostic pillars (assessment gaps + IBS/MVB fragility
 * + scenario breaches). Read-only; actions route to existing surfaces.
 */
import {
  useQueryClient,
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type { EngineGrounding, RecommendationsReport } from "shared/api/types";

export function useRecommendations(
  limit = 50,
): UseQueryResult<RecommendationsReport, ApiError> {
  return useQuery({
    queryKey: ["recommendations", limit],
    queryFn: () => api.get<RecommendationsReport>(`/v1/recommendations?limit=${limit}`),
  });
}

interface DraftCapResult {
  cap_id: string;
  framework_code: string;
  control_natural_id: string;
  linked_controls: number;
  /** Round 14 A5 -- false when the control was already on a draft plan
   *  and the server returned that plan instead of creating one. */
  created?: boolean;
}

/** What the chained draft returns to the view: the CAP, plus how the
 *  best-effort Engine action-drafting step went. */
export interface DraftCapOutcome extends DraftCapResult {
  /** Number of Engine-drafted action items (undefined when not attempted). */
  actions?: number;
  /** True when the Engine isn't configured (503) -- CAP drafted, no actions. */
  engineOff?: boolean;
  /** ROUND 15 A10 -- the Engine was asked and did not answer (502). */
  engineRefused?: boolean;
  /** Round 13 F7 -- the grounding verdict for the actions drafted on
   *  the way. The control drawer's own drafting verb drafted actions
   *  and printed no grounding line at all, while the CAP's drafting
   *  verb printed one with the nominal-attribution warning. Round 12
   *  recorded the same asymmetry. */
  grounding?: EngineGrounding;
}

/** P194 (+ one-click follow-on): draft a remediation CAP from an
 *  assessment-gap control AND, when `withEngine`, run the P164 Engine
 *  action-drafter on it so the CAP arrives with grounded suggested actions.
 *  The CAP draft must succeed; the Engine step is best-effort (a missing
 *  Engine / failure still resolves with the CAP). */
export function useDraftCapFromControl(): UseMutationResult<
  DraftCapOutcome,
  ApiError,
  { controlId: string; withEngine?: boolean; force?: boolean; title?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: async ({ controlId, withEngine, force, title }) => {
      const cap = await api.post<DraftCapResult>(
        "/v1/framework/caps/draft-from-control",
        // ROUND 15 A18 -- `force` answers the route's question about a
        // control already on a plan that has moved past draft.
        // Round 19 G41 -- the plan's name, when the raiser gave one.
        {
          control_id: controlId,
          ...(force ? { force: true } : {}),
          ...(title?.trim() ? { title: title.trim() } : {}),
        },
      );
      // ROUND 14 A5 -- a plan that already existed is somebody's plan,
      // usually carrying other controls. Running the drafter on it put
      // four Engine actions about OTHER controls into it, three under a
      // different framework, under a toast saying a CAP was drafted.
      // Reusing the plan is right (H1); adding to it unasked is not.
      if (!withEngine || cap.created === false) return cap;
      try {
        const res = await api.post<{
          entries: unknown[];
          grounding?: EngineGrounding;
        }>(`/v1/framework/caps/${cap.cap_id}/draft-actions`);
        return {
          ...cap,
          actions: res.entries.length,
          // F7 -- carried rather than discarded. It was on the wire
          // all along; nothing read it on this path.
          ...(res.grounding ? { grounding: res.grounding } : {}),
        };
      } catch (e) {
        // Best-effort: the CAP exists regardless. 503 = Engine not
        // configured; 502 = it was asked and did not answer (A10), which
        // the toast must say rather than "drafted".
        // ROUND 16 A22 -- any answer other than "not configured" is the
        // Engine failing to draft: the plan must say so either way.
        const status = (e as ApiError)?.status;
        return { ...cap, engineOff: status === 503, engineRefused: status !== 503 };
      }
    },
    /* Round 19 G38 -- a plan raised from a control changes its letter's
       page, the plan registers and the threads that count plans. */
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["caps"] });
      void qc.invalidateQueries({ queryKey: ["letters"] });
      void qc.invalidateQueries({ queryKey: ["attestation-links"] });
      void qc.invalidateQueries({ queryKey: ["thread"] });
    },
  });
}
